import { useState, useEffect, useCallback } from 'react';
import { Fab, IconButton } from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from 'react-router';
import { dictionary } from '../../dictionary';
import { palette } from '../../theme';
import { contactActions } from './contactActions';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

const FAB_SIZE = 56;
const ICON_SIZE = 44;
const GAP = 12;
const FAB_GAP = 8;

const allActions = [
  ...contactActions,
  {
    label: dictionary.general.floatingCta.leaveDetails,
    icon: <DescriptionOutlinedIcon />,
    href: '/contact',
    isRoute: true,
  },
];

export default function FloatingCta() {
  const [open, setOpen] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;

    const observer = new IntersectionObserver(([entry]) => setFooterVisible(entry.isIntersecting), { threshold: 0 });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-floating-cta]')) return;
      close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, close]);

  const handleActionClick = (action: (typeof allActions)[number]) => {
    close();
    if ('isRoute' in action && action.isRoute) {
      navigate(action.href);
    }
  };

  const bottomBase = footerVisible ? 100 : 24;

  return (
    <div
      data-floating-cta
      style={{ position: 'fixed', bottom: bottomBase, left: 24, zIndex: 1300, transition: 'bottom 0.3s ease' }}
    >
      {allActions.map((action, i) => {
        const offset = FAB_SIZE + FAB_GAP + i * (ICON_SIZE + GAP);
        const isRoute = 'isRoute' in action && action.isRoute;
        const external = 'external' in action && action.external;

        return (
          <IconButton
            key={action.label}
            aria-label={action.label}
            {...(!isRoute
              ? {
                  component: 'a' as const,
                  href: action.href,
                  ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
                }
              : {})}
            onClick={() => handleActionClick(action)}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: (FAB_SIZE - ICON_SIZE) / 2,
              width: ICON_SIZE,
              height: ICON_SIZE,
              backgroundColor: palette.white,
              border: `1px solid ${palette.border}`,
              color: palette.secondary,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              transition: `transform 0.3s ease ${i * 50}ms, opacity 0.3s ease ${i * 50}ms, filter 0.3s ease ${i * 50}ms`,
              transform: open ? `translateY(-${offset}px) scale(1)` : 'scale(0.5)',
              opacity: open ? 1 : 0,
              filter: open ? 'blur(0)' : 'blur(4px)',
              pointerEvents: open ? 'auto' : 'none',
              '&:hover': {
                backgroundColor: palette.backgroundSections,
                color: palette.secondary,
              },
            }}
          >
            {action.icon}
          </IconButton>
        );
      })}

      <Fab
        onClick={() => setOpen((prev) => !prev)}
        aria-label={dictionary.general.leaveDetails}
        aria-expanded={open}
        sx={{
          position: 'relative',
          backgroundColor: palette.secondary,
          color: palette.white,
          transition: 'transform 0.3s ease',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          '&:hover': {
            backgroundColor: palette.primary,
          },
        }}
      >
        {open ? <AddOutlinedIcon /> : <SmsOutlinedIcon />}
      </Fab>
    </div>
  );
}
